import { memo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronsUpDown, HelpCircle } from 'lucide-react'

import { teacherNav, classTeacherNav } from './nav-items'
import { useRole } from './RoleContext'
import { Badge } from '@/components/ui/badge'
import { ABSENCE_NOTES, STUDENT_ATTENDANCE_STATS } from '../data/class-teacher-mock'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar'

interface School {
  id: string
  name: string
  initials: string
  color: string
}

function SchoolLogo({ school, size = 'md' }: { school: School; size?: 'sm' | 'md' }) {
  const sizeClass = size === 'sm' ? 'size-6 text-[0.5rem]' : 'size-8 text-xs'
  return (
    <div
      className={`${school.color} flex shrink-0 items-center justify-center rounded-lg font-bold tracking-tight text-white ${sizeClass}`}
      aria-hidden
    >
      {school.initials}
    </div>
  )
}

function SchoolSwitcher({
  schools,
  selectedSchool,
  onSelect,
}: {
  schools: School[]
  selectedSchool: School
  onSelect: (school: School) => void
}) {
  const { t } = useTranslation()

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <SchoolLogo school={selectedSchool} />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{selectedSchool.name}</span>
                <span className="text-muted-foreground truncate text-xs">
                  {t('ekoolTeacher.sidebarSubtitle')}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-muted-foreground text-xs">
                {t('ekoolTeacher.switchSchool')}
              </DropdownMenuLabel>
              {schools.map((school) => (
                <DropdownMenuItem
                  key={school.id}
                  onClick={() => onSelect(school)}
                  className="gap-2 p-2"
                >
                  <SchoolLogo school={school} size="sm" />
                  {school.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

export const AppSidebar = memo(function AppSidebar({
  schools,
  selectedSchool,
  onSelectSchool,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  schools: School[]
  selectedSchool: School
  onSelectSchool: (school: School) => void
}) {
  const { t } = useTranslation()
  const { role } = useRole()
  const location = useLocation()
  const items = role === 'classTeacher' ? classTeacherNav : teacherNav
  const pendingExcusesCount = ABSENCE_NOTES.filter((n) => n.status === 'pending').length
  const attendanceAlertCount = STUDENT_ATTENDANCE_STATS.filter((s) => s.alertTier !== 'none').length

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SchoolSwitcher
          schools={schools}
          selectedSchool={selectedSchool}
          onSelect={onSelectSchool}
        />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t('nav.navigation')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => {
                const label = t(item.titleKey)
                const badge =
                  item.titleKey === 'ekoolClassTeacher.nav.absenceNotes'
                    ? pendingExcusesCount
                    : item.titleKey === 'ekoolClassTeacher.nav.attendance'
                      ? attendanceAlertCount
                      : 0
                return (
                  <SidebarMenuItem key={item.titleKey}>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname.startsWith(item.url)}
                      tooltip={label}
                    >
                      <NavLink to={item.url}>
                        <item.icon className="!size-5" strokeWidth={1.5} />
                        <span>{label}</span>
                        {badge > 0 && (
                          <Badge
                            variant="destructive"
                            className="ml-auto h-4 px-1.5 text-[0.6rem] tabular-nums"
                          >
                            {badge}
                          </Badge>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton tooltip={t('ekoolTeacher.nav.help')}>
              <HelpCircle className="!size-5" strokeWidth={1.5} />
              <span>{t('ekoolTeacher.nav.help')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
})
